import { useState } from "react";
import Content from "../../common/page/Content";
import BaseMap, { MapTypeId } from "../../core/Map";
import { LeadList, SearchRegion } from "../../../types/neighborhoodSearch";
import SearchRegionShape from "../components/SearchRegionShape";
import { SavedSearch } from "../../../types/neighborhoodSearch";
import ListingPropertyMarker from "../../listings/ListingPropertyMarker";
import { Listing, EnumProductTypes } from "../../../types/listing";
import { GeoLocation } from "../../../types/common";
import { DEFAULT_ZOOM } from "../../../constants/map";
import { useNeighborhoodSearchSettingsQuery } from "src/hooks/useNeighborhoodSearchSettings";

const Map = ({
  searches,
  listings,
  selectedListing,
  mapCenter,
  onListingClick,
  isHideDNCPhoneNumbers,
}: {
  searches: LeadList[];
  listings: Listing[];
  selectedListing: Listing | undefined;
  mapCenter: GeoLocation | undefined;
  onListingClick: (listing: Listing | null) => void;
  isHideDNCPhoneNumbers?: boolean | null | undefined;
}) => {
  const savedSearches = searches as SavedSearch[];
  const firstSearch = savedSearches?.[0];
  const initialSearchRegion = firstSearch?.search_parameters?.searchRegion;
  const { data: settings } = useNeighborhoodSearchSettingsQuery();

  const [hoveredListing, setHoveredListing] = useState<Listing | null>(null);

  const noLocationMessage = selectedListing?.title ? (
    <>
      No location data for
      <br />
      <span className="font-semibold">{selectedListing.title}</span>
    </>
  ) : (
    "No location data available"
  );

  return (
    <Content>
      <div className="h-full w-full">
        <BaseMap
          noLocationMessage={noLocationMessage}
          initialMapTypeId={settings?.defaultMapTypeId || MapTypeId.Roadmap}
        mapCenter={mapCenter || { lat: 0, lng: 0 }}
        zoom={initialSearchRegion?.zoom || DEFAULT_ZOOM}
      >
        {savedSearches?.map((savedSearch) => {
          const regions: Array<{ region: SearchRegion; label: string; key: string }> = [];

          // Add search region for a regular saved search
          if (savedSearch?.search_parameters?.searchRegion) {
            regions.push({
              region: savedSearch.search_parameters.searchRegion,
              label: savedSearch.name,
              key: `saved-search-region-${savedSearch.id}`,
            });
          }

          // Add regions from combined search sources with their original names
          savedSearch?.search_parameters?.combinedSearchSources?.forEach((source, sourceIndex) => {
            if (source.searchRegion) {
              regions.push({
                region: source.searchRegion,
                label: source.searchName,
                key: `combined-search-region-${savedSearch.id}-${sourceIndex}`,
              });
            }
          });

          return regions.map(({ region, label, key }) => (
            <SearchRegionShape
              key={key}
              {...region}
              label={label}
              showLabel={true}
            />
          ));
        })}
        {(listings || []).map((listing, index) => {
          const geoLocation = listing?.geolocation;
          if (geoLocation) {
            return (
              <ListingPropertyMarker
                listingId={listing.id}
                productType={EnumProductTypes.ns}
                isSelected={selectedListing?.id === listing.id}
                key={`property-marker-${index}`}
                position={{
                  lat: geoLocation.lat,
                  lng: geoLocation.lng,
                }}
                isHovered={hoveredListing?.id === listing.id}
                onMouseOver={() => setHoveredListing(listing)}
                onMouseOut={() => setHoveredListing(null)}
                onClick={() => onListingClick(listing)}
                isHideDNCPhoneNumbers={isHideDNCPhoneNumbers}
              />
            );
          }
          return null;
        })}
        </BaseMap>
      </div>
    </Content>
  );
};

export default Map;
